import { Dialog, DialogContent, DialogHeader, DialogTitle } from "components/app/dialog/dialog";
import { Card } from "components/ui/card";
import { Pause, Play, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { logoFlipFlop } from "assets";
import useDialogMusicStore from "store/dialog/useDialogMusicStore";
import { useEffect, useRef, useState } from "react";
import { Service } from "utils/services";

import { CircularProgressbarWithChildren, buildStyles } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import SearchBar from "components/app/search-bar/SearchBar";

interface DialogProps {
  setIsDialogOpen: (value: boolean) => void;
  isOpen: boolean;
  onSelectedMusic: (music: any) => void;
}

export function DialogMusic({ setIsDialogOpen, isOpen, onSelectedMusic }: DialogProps) {
  const { t } = useTranslation();

  const { musicGenress, musics, musicsRecord, isLoadingPage, selectedGenre, initializePage, disposePage, fetchMusics, toggleGenre } = useDialogMusicStore();

  const [currentSongUrl, setCurrentSongUrl] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const target = loadMoreRef.current;

    if (!target) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          if (musicsRecord.canLoadMore()) {
            fetchMusics(false, "");
          }
        }
      },
      {
        root: null,
        rootMargin: "0px",
        threshold: 1.0,
      }
    );

    observer.observe(target);

    return () => {
      observer.unobserve(target);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [musics]);

  useEffect(() => {
    initializePage();

    return () => {
      disposePage();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Dialog open={isOpen} onOpenChange={setIsDialogOpen}>
      <DialogContent isShowBtnXClose={false} className="max-w-full lg:max-w-[700px] h-[calc(100vh-80px)] py-2 px-0 flex flex-col " onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="mt-2 text-center">{t("Musics")}</DialogTitle>

          <div
            className="absolute flex items-center justify-center right-3 top-1 z-40 cursor-pointer rounded-full hover:bg-accent hover:text-accent-foreground w-[36px] h-[36px] p-2"
            onClick={() => setIsDialogOpen(false)}
          >
            <X />
          </div>
        </DialogHeader>

        {isLoadingPage ? (
          <div className="flex flex-col w-full h-full overflow-auto space-y-4 p-4">Loading...</div>
        ) : (
          <div className="flex flex-col w-full h-full overflow-auto space-y-4 pl-4 pr-2 pb-4" style={{ scrollbarGutter: "stable" }}>
            <div className="sticky top-0 z-10 bg-background backdrop-blur-md shadow-sm">
              <div className="pt-2">
                <SearchBar
                  className="mb-0"
                  onValueChange={(value) => {
                    if (currentSongUrl && isPlaying) {
                      audioRef.current?.pause();
                    }

                    fetchMusics(true, value);
                  }}
                />
              </div>
            </div>
            {/* Album Grid */}
            <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-6 gap-2 w-full">
              {musicGenress.map((genre) => {
                const isSelected = selectedGenre?.id === genre.id;

                return (
                  <button
                    key={genre.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (currentSongUrl && isPlaying) {
                        audioRef.current?.pause();
                      }
                      toggleGenre(genre);
                    }}
                    className={`w-full h-[90px] flex flex-col items-center justify-center gap-1 rounded-xl  transition-all border-2
    ${isSelected ? "border-primary" : "border-border"}
    hover:border-primary`}
                  >
                    {genre.icon && <img src={genre.icon} alt={genre.name} className="w-8 h-8 object-contain" />}
                    <span className="text-xs text-center">{genre.name}</span>
                  </button>
                );
              })}
            </div>

            {/* List of music */}
            <div className="space-y-2">
              {musicsRecord.isLoading() ? (
                <div>Loading music</div>
              ) : musics.length === 0 ? (
                <>No data</>
              ) : (
                musics.map((music, index) => (
                  <Card key={music.id} className="p-2 rounded-md w-full cursor-pointer me-2 relative">
                    <div className="flex gap-3 items-center w-full">
                      {/* Image Thumbnail */}
                      <div className="w-14 h-14">
                        <img src={music.thumbnail ?? logoFlipFlop} alt={music.name} className="w-14 h-14 rounded-md object-cover" />
                      </div>

                      {/* Sound Details (clickable for play/pause too) */}
                      <div onClick={() => onSelectedMusic(music)} className="flex-1 space-y-1">
                        <p className="text-md font-semibold line-clamp-1">{music?.sound_title}</p>
                        <p className="text-muted-foreground text-xs font-semibold line-clamp-1">
                          {music?.sound_singer || "Unknown"} • {music?.sound_genres || "None"} • {Service.formatSoundDuration(music.sound_duration)} • {music?.usage_count} Used
                        </p>
                        <p className="text-muted-foreground text-sm font-semibold line-clamp-1">{music?.hashtag}</p>
                      </div>

                      {/* Play/Pause with Circular Progress on Right */}
                      <div className="w-10 h-10 ml-auto">
                        <CircularProgressbarWithChildren
                          value={currentSongUrl === music.sound_url && duration ? (currentTime / duration) * 100 : 0}
                          styles={buildStyles({
                            pathColor: "hsl(var(--primary))",
                            trailColor: "hsl(var(--muted))",
                            strokeLinecap: "round",
                          })}
                          strokeWidth={6}
                        >
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (currentSongUrl === music.sound_url && isPlaying) {
                                audioRef.current?.pause();
                              } else if (currentSongUrl === music.sound_url && !isPlaying) {
                                audioRef.current?.play();
                              } else {
                                setCurrentSongUrl(music.sound_url);
                                setIsPlaying(true);
                              }
                            }}
                            className="text-white bg-black/60 rounded-full w-7 h-7 flex items-center justify-center"
                          >
                            {currentSongUrl === music.sound_url && isPlaying ? <Pause size={14} /> : <Play size={14} />}
                          </button>
                        </CircularProgressbarWithChildren>
                      </div>
                    </div>
                  </Card>
                ))
              )}

              <div ref={loadMoreRef} className="h-4" />
            </div>
          </div>
        )}

        {currentSongUrl && (
          <audio
            ref={audioRef}
            src={currentSongUrl}
            autoPlay
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onTimeUpdate={() => {
              setCurrentTime(audioRef.current?.currentTime || 0);
            }}
            onLoadedMetadata={() => {
              setDuration(audioRef.current?.duration || 0);
            }}
            onEnded={() => setIsPlaying(false)}
            className="hidden"
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

